import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

export const initialState = {
  user: null, // Get from ClientUserController in API
  users: [],
  connectionState: 'disconnected',
  servicesStatus: {},
  versionRefreshModalIsOpen: false,
  errorListModalState: null,
  showErrorListModal: false,
  appSettings: {
    isLoading: true
  },
  confirmationModalState: {
    isModalOpen: false
  },
  appNotifications: [],
  userLocation: null,
  signalRQueuedUpdates: [],
  gridState: {},
  isMobile: null,
  displayMode: null
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'appSlice',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setConnectionState: (state, action) => {
      state.connectionState = action.payload;
    },
    setServicesStatus: (state, action) => {
      state.servicesStatus = action.payload;
    },
    setVersionRefreshModalIsOpen: (state, action) => {
      state.versionRefreshModalIsOpen = action.payload;
    },
    setAppSettings: (state, action) => {
      state.appSettings = action.payload;
    },
    setConfirmationModalState: (state, action) => {
      state.confirmationModalState = action.payload;
    },
    setErrorListModalState: (state, action) => {
      state.errorListModalState = action.payload;
    },
    setShowErrorListModal: (state, action) => {
      state.showErrorListModal = action.payload;
    },
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setDisplayMode: (state, action) => {
      state.displayMode = action.payload;
    },
    appNotify: { 
      reducer: (state, action) => {
        state.appNotifications.push({ ...action.payload });
      },
      prepare: (message, variant, options, actions) => {
        // If caller doesn't provide a key, we assign one here
        const key = options && options.key;
        const newKey = key || new Date().getTime() + Math.random();
        return {
          payload: {
            key: newKey,
            message,
            options: {
              ...options,
              variant: variant,
              key: newKey  // it is very important to pass the key here so that notistack can pass the correct key when removing snackbar from Redux store
            },
            actions: { 
              ...actions
            }
          }
        }
      }
    },
    closeSnackbar: (state, action) => {
      state.appNotifications = state.appNotifications.map(notification => (notification.key === action.payload) ? { ...notification, dismissed: true } : { ...notification });
    },
    removeSnackbar: (state, action) => {
      state.appNotifications = state.appNotifications.filter(notification => notification.key !== action.payload);
    },
    setUserLocation: (state, action) => {
      state.userLocation = action.payload;
    },
    addSignalRQueuedUpdate: (state, action) => {
      state.signalRQueuedUpdates.push(action.payload);
    },
    removeSignalRQueuedUpdates: (state, action) => {
      state.signalRQueuedUpdates = state.signalRQueuedUpdates.filter(update => update.updateTarget !== action.payload);
    },
    setGridStateById: {
      reducer: (state, action) => {
        const { id, updateState } = action.payload;
        return {
          ...state,
          gridState: {
            ...state.gridState,
            [id]: updateState
          }
        }
      },
      prepare: (id, updateState) => ({ payload: { id, updateState } })
    },

    // Redux saga actions, no reducers
    checkVersion: () => { }, // Triggers versionCheckSaga
    fetchAppSettings: () => { }, // Triggers getAppSettingsSaga
    pingServices: () => { }, // Triggers pingServicesSaga
    fetchUsers: () => { }, // Triggers getUsersSaga
    fetchUser: () => { }, // Triggers getUserSaga
    updateClientTxnOppsFromQueued: () => { }, // Triggers clientTxnOppsUpdateSaga
  }
});

export default slice.reducer;

export const appActions = slice.actions;

// Memoized selectors
export const appSelectors = {
  userSelector: createSelector(
    state => state.appState.user,
    user => user
  ),
  userRoleAuctionUnderwriteSelector: createSelector(
    state => state.appState.user,
    user => user && user.roles && !!user.roles.auctionUnderwrite
  ),
  userRoleUnderwriteManagerSelector: createSelector(
    state => state.appState.user,
    user => user && user.roles && !!user.roles.underwriteManager
  ),
  userRoleExploreSelector: createSelector(
    state => state.appState.user,
    user => user && user.roles && !!user.roles.exploreUser
  ),
  userRoleSourcingUserSelector: createSelector(
    state => state.appState.user,
    user => user && user.roles && !!user.roles.sourcingUser
  ),
  userRoleSourcingManagerSelector: createSelector(
    state => state.appState.user,
    user => user && user.roles && !!user.roles.sourcingManager
  ),
  servicesStatusSelector: createSelector(
    state => state.appState.servicesStatus,
    servicesStatus => servicesStatus
  ),
  appSettingsSelector: createSelector(
    state => state.appState.appSettings,
    appSettings => appSettings
  ),
  signalRQueuedUpdatesSelector: createSelector(
    state => state.appState.signalRQueuedUpdates,
    signalRQueuedUpdates => signalRQueuedUpdates
  )
}
